import React from "react";
import { Reveal } from "@/components/Reveal";

/**
 * ManifestoShowreel (copy-only variant)
 * -------------------------------------
 * The photograph (or optional video) is now painted by the shared
 * <HomeCinematicBackground /> that lives behind ALL home sections —
 * so the same take carries through from the Hero into the Manifesto
 * with zero visible boundary. This component renders only:
 *   • An editorial darkening wash focused on the copy column
 *   • The "Chapter I — Manifesto" eyebrow, headline, and body
 *
 * We keep the section's own min-h-screen so the visitor scrolls
 * through a "chapter's worth" of the sticky photograph before the
 * Directory begins.
 */

export function ManifestoShowreel() {
  return (
    <section
      data-testid="manifesto-showreel"
      className="relative min-h-screen bg-transparent flex flex-col justify-center"
      aria-label="Studio manifesto"
    >
      {/* Editorial wash — an angled darkening anchored to the copy column
          (left). Deep enough to keep the serif headline readable against
          any showreel frame, transparent on the right so the photograph
          still breathes. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(105deg, rgba(15,12,11,0.78) 0%, rgba(15,12,11,0.58) 34%, rgba(15,12,11,0.28) 62%, rgba(15,12,11,0.10) 84%, rgba(15,12,11,0) 100%)",
        }}
      />

      {/* Bottom uniform darken — smooths the transition into the Directory
          section by adding a subtle even-across-the-width fade. Without
          this, the angled left-heavy wash meets the Directory's uniform
          heavy wash and creates a visible brightness inversion at the
          seam (left brightens, right darkens). */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-[30vh] pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(15,12,11,0) 0%, rgba(15,12,11,0.14) 45%, rgba(15,12,11,0.34) 100%)",
        }}
      />

      {/* Copy — preserved exactly. */}
      <div className="relative z-10 w-full">
        <div className="px-6 md:px-12 lg:px-16 py-40 md:py-56 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
            <div className="md:col-span-4">
              <Reveal>
                <p
                  data-testid="manifesto-eyebrow"
                  className="text-[10px] tracking-[0.42em] uppercase text-ink-champagne"
                >
                  Chapter I — Manifesto
                </p>
              </Reveal>
            </div>
            <div className="md:col-span-8">
              <Reveal delay={200}>
                <p
                  data-testid="manifesto-headline"
                  className="font-display text-3xl md:text-5xl leading-[1.1] tracking-tight text-ink-ivory"
                >
                  A wedding is not a production.
                  <br />
                  It is the slow, accidental composition of a life.
                </p>
              </Reveal>
              <Reveal delay={500}>
                <p
                  data-testid="manifesto-body"
                  className="mt-10 md:mt-14 text-base md:text-lg leading-relaxed text-ink-ivory/90 max-w-2xl"
                >
                  Cineglimpse Stories is a studio that observes more than it
                  directs. Our films and photographs aspire to the stillness of a
                  printed page — quiet enough to listen, patient enough to find the
                  frame that matters most. We work with couples who treat
                  memory as something worth keeping for a hundred years.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
